import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import TrackingRegister from './TrackingRegister';
import { useUploadedFiles } from '../UploadedFilesContext';

interface ProductRule {
  id: string;
  source_keyword: string;
  target_name: string;
}

interface OrderRow {
  productOrderNumber: string;
  recipientName: string;
  recipientPhone: string;
  recipientPhone2: string;
  address: string;
  productName: string;
  originalOption: string;
  quantity: number;
  memo: string;
  buyerName: string;
  senderName: string;
  senderPhone: string;
  senderAddress: string;
  merged?: boolean;
}

interface VendorTemplate {
  columns: { header: string; key: string; defaultValue?: string }[];
  senderName: string;
  senderPhone: string;
  senderAddress: string;
}

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

interface SmsTemplateDef { label: string; body: string; productNameIncludes?: string[]; }

export default function OrderGeneratorTab({ vendorCode, vendorName, template, courier, consolidate, filenameDateOffset, excludeRules, smsTemplates }: { vendorCode: string; vendorName: string; template: VendorTemplate; courier: string; consolidate: boolean; filenameDateOffset: number; excludeRules: { triggerKeywords: string[]; excludeKeywords: string[] }[]; smsTemplates: SmsTemplateDef[] }) {
  const [rules, setRules] = useState<ProductRule[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState('');
  const [filename, setFilename] = useState('');
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  const fileRef = useRef<HTMLInputElement>(null);
  const { files: ctxFiles, password, setPassword } = useUploadedFiles();
  const autoProcessedRef = useRef(false);
  const [includeAddressInKatalk, setIncludeAddressInKatalk] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const [rulesLoaded, setRulesLoaded] = useState(false);

  // 치환규칙 로드
  useEffect(() => {
    setRulesLoaded(false);
    fetch(`/api/product-rules?vendor=${encodeURIComponent(vendorCode)}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setRules(Array.isArray(d) ? d : []); setRulesLoaded(true); })
      .catch(() => setRulesLoaded(true));
  }, [vendorCode]);

  // 오늘의 주문서에서 공유된 업로드 파일이 있으면 규칙 로드 후 자동 처리 (파트너 전환 시에도 동일 파일 재사용)
  useEffect(() => {
    if (!rulesLoaded || autoProcessedRef.current) return;
    if (!ctxFiles || ctxFiles.length === 0) return;
    autoProcessedRef.current = true;
    processFiles(ctxFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesLoaded, ctxFiles]);

  // 상품명 치환
  const convertProductName = useCallback((optionInfo: string): string => {
    // 키워드 수 많은 규칙 우선 (더 구체적인 규칙 먼저)
    const sorted = [...rules].sort((a, b) =>
      b.source_keyword.split(',').length - a.source_keyword.split(',').length
    );

    for (const rule of sorted) {
      const keywords = rule.source_keyword.split(',').map(k => k.trim()).filter(Boolean);
      if (keywords.every(kw => optionInfo.includes(kw))) {
        return rule.target_name;
      }
    }
    return optionInfo; // 치환 불가 시 원본
  }, [rules]);

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setLoading(true);
    setFilename(files.map(f => f.name).join(', '));

    const token = sessionStorage.getItem('admin-token') || '';
    const results = await Promise.all(files.map(async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', password);
      fd.append('vendorCode', vendorCode);
      fd.append('excludeRules', JSON.stringify(excludeRules));
      const res = await fetch('/api/vendor/extract', { method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const msg = await res.text();
        return { ok: false as const, name: file.name, msg };
      }
      const data = await res.json();
      return { ok: true as const, name: file.name, rows: data.rows as any[] };
    }));

    const failed = results.filter(r => !r.ok) as Extract<typeof results[number], { ok: false }>[];
    if (failed.length > 0) {
      alert(failed.map(f => `${f.name}: ${f.msg}`).join('\n'));
    }

    // 백엔드에서 excludeRules 적용 완료 — 여기서는 결과만 병합
    const allRows = results.flatMap(r => r.ok ? r.rows : []);
    const extracted: OrderRow[] = allRows.map((r: any) => {
      const converted = convertProductName(r.optionInfo);
      return {
        productOrderNumber: r.productOrderNumber || '',
        recipientName: r.recipientName,
        recipientPhone: r.recipientPhone,
        recipientPhone2: r.recipientPhone2 || '',
        address: r.address,
        productName: converted,
        originalOption: r.optionInfo,
        quantity: r.quantity,
        memo: r.memo || '',
        buyerName: r.buyerName || '',
        senderName: template.senderName,
        senderPhone: template.senderPhone,
        senderAddress: template.senderAddress,
      };
    });

    const unmatched = extracted.filter(o => o.productName === o.originalOption).length;
    setOrders(extracted);
    setUnmatchedCount(unmatched);
    setLoading(false);
    const okCount = results.filter(r => r.ok).length;
    showToast(`${okCount}개 파일 · ${extracted.length}건 추출${unmatched > 0 ? ` (미치환 ${unmatched}건)` : ''}`);
  };

  // 상품명에 이미 "N개" 가 있으면 N × qty 로 곱해서 표기 (예: "...500g 2개" + qty=2 → "...500g 4개")
  const formatProductWithQty = useCallback((name: string, qty: number): string => {
    if (!qty || qty <= 1) return name;
    const m = name.match(/^(.*?)\s(\d+)개\s*$/);
    if (m) {
      const total = parseInt(m[2], 10) * qty;
      return `${m[1]} ${total}개`;
    }
    return `${name} ${qty}개`;
  }, []);

  // 수취인(이름+연락처+주소) 동일 시 합배송 1건으로 묶음
  const consolidatedOrders = useMemo<OrderRow[]>(() => {
    if (!consolidate || orders.length === 0) return orders;
    const grouped = new Map<string, OrderRow[]>();
    for (const o of orders) {
      const key = `${o.recipientName}|${o.recipientPhone}|${(o.address || '').trim()}`;
      const list = grouped.get(key) || [];
      list.push(o);
      grouped.set(key, list);
    }
    return Array.from(grouped.values()).map(list => {
      if (list.length === 1) return list[0];
      // 동일 상품끼리 수량 합산. 수량 1개일 땐 인라인 표기 생략, 2개 이상만 " N개"로 표기. 줄바꿈으로 결합.
      const byProduct = new Map<string, number>();
      for (const o of list) {
        byProduct.set(o.productName, (byProduct.get(o.productName) || 0) + (o.quantity || 1));
      }
      const productLine = Array.from(byProduct.entries())
        .map(([name, qty]) => formatProductWithQty(name, qty))
        .join('\n');
      return {
        ...list[0],
        productName: productLine,
        quantity: 1,
        memo: list.map(o => o.memo).filter(Boolean).join(' / '),
        merged: true,
      };
    });
  }, [orders, consolidate, formatProductWithQty]);

  const downloadExcel = async () => {
    if (orders.length === 0) return;
    const exportOrders: OrderRow[] = consolidate ? consolidatedOrders : orders;

    const res = await fetch('/api/vendor/export', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ orders: exportOrders, template, filename }),
    });

    if (!res.ok) { alert('다운로드 실패'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const d = new Date();
    d.setDate(d.getDate() + filenameDateOffset);
    const dateStr = `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
    a.download = `${dateStr} 제주로부터-${vendorName} 주문서.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('주문서가 다운로드되었습니다');
  };

  return (
    <div>
      {/* 업로드 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-end gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">원본 비밀번호</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-1 focus:ring-jeju-brown" />
          </div>
          {orders.length > 0 && (
            <>
              <div className="text-sm text-gray-500">추출: <strong className="text-gray-800">{orders.length}건</strong></div>
              {unmatchedCount > 0 && <div className="text-sm text-orange-500">미치환: {unmatchedCount}건</div>}
            </>
          )}
        </div>
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const fs = Array.from(e.dataTransfer.files); if (fs.length) processFiles(fs); }}
          onClick={() => fileRef.current?.click()}
          className={`flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            dragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple className="hidden" onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) processFiles(fs); e.target.value = ''; }} />
          {loading ? (
            <p className="text-sm text-gray-500">처리 중...</p>
          ) : (
            <>
              <div className="text-3xl mb-2 opacity-30">📊</div>
              <p className="text-sm text-gray-400">스마트스토어 원본 파일을 드래그하거나 클릭하여 선택하세요 (여러 개 가능)</p>
              <p className="text-xs text-gray-300 mt-1">{vendorCode}- 코드 상품만 자동 추출됩니다</p>
            </>
          )}
        </div>
      </div>

      {/* 결과 테이블 */}
      {orders.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              총 <strong className="text-gray-800">{consolidatedOrders.length}</strong>건
              {consolidate && consolidatedOrders.length !== orders.length && (
                <span className="ml-2 text-xs text-gray-400">(원본 {orders.length}건 → 합배송 {consolidatedOrders.length}건)</span>
              )}
            </div>
            <button
              onClick={downloadExcel}
              className="px-5 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors"
            >엑셀 다운로드</button>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-480px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">받는사람</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">연락처</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">주소</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">상품명</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">수량</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">배송메세지</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {consolidatedOrders.map((o, i) => (
                  <tr key={i} className={`hover:bg-gray-50 transition-colors ${o.productName === o.originalOption ? 'bg-yellow-50/50' : ''}`}>
                    <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{o.recipientName}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{o.recipientPhone}</td>
                    <td className="px-4 py-2.5 text-gray-600 max-w-[200px] truncate" title={o.address}>{o.address}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={o.productName === o.originalOption ? 'text-orange-500' : 'text-gray-800'}>{o.productName}</span>
                      {o.productName === o.originalOption && <span className="text-[10px] text-orange-400 ml-1">(미치환)</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-800 text-right">{o.quantity}</td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-[150px] truncate" title={o.memo}>{o.memo || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 영성수산 카톡주문 형식 (vendorCode === 'YS' 만) */}
      {vendorCode === 'YS' && consolidatedOrders.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-gray-800">카톡주문</div>
              <div className="text-xs text-gray-400 mt-0.5">각 항목을 카카오톡에 붙여넣을 수 있는 형식입니다</div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeAddressInKatalk}
                  onChange={(e) => setIncludeAddressInKatalk(e.target.checked)}
                  className="accent-jeju-brown"
                />
                주소 포함
              </label>
              <button
                type="button"
                onClick={async () => {
                  const all = consolidatedOrders.map(o => {
                    const line = o.merged ? o.productName : formatProductWithQty(o.productName, o.quantity);
                    const addressPart = includeAddressInKatalk && o.address ? `\n${o.address}` : '';
                    return `${o.recipientName} //\n${o.recipientPhone}${addressPart}\n\n${line}`;
                  }).join('\n\n────────\n\n');
                  try {
                    await navigator.clipboard.writeText(all);
                    showToast(`${consolidatedOrders.length}건 전체 복사됨`);
                  } catch {
                    alert('복사 실패');
                  }
                }}
                className="px-4 py-2 text-xs border border-jeju-brown text-jeju-brown rounded-lg hover:bg-jeju-brown/5"
              >전체 복사</button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {consolidatedOrders.map((o, i) => {
              const productLine = o.merged ? o.productName : formatProductWithQty(o.productName, o.quantity);
              const addressPart = includeAddressInKatalk && o.address ? `\n${o.address}` : '';
              const katalk = `${o.recipientName} //\n${o.recipientPhone}${addressPart}\n\n${productLine}`;
              return (
                <div key={i} className="px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed flex-1">{katalk}</pre>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(katalk);
                        showToast('복사되었습니다');
                      } catch {
                        alert('복사 실패');
                      }
                    }}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-white whitespace-nowrap"
                  >복사</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 운송장 등록 */}
      {orders.length > 0 && (
        <TrackingRegister
          orders={orders}
          courier={courier}
          vendorCode={vendorCode}
          productRules={rules}
          legacyTemplates={smsTemplates}
          onSuccess={showToast}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
